import { zodResolver } from '@hookform/resolvers/zod';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ProgressButton from '@/components/common/progress-button';
import CreateGroupForm from '@/components/contacts/create-group-form';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useUploadFile from '@/hooks/files/use-upload-file';
import useCreateGroup, { type CreateGroupFormData, createGroupFormScheme } from '@/hooks/groups/use-create-group';
import { useIsDesktop } from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import { getUserId } from '@/stores/auth';

export default function CreateGroupRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const isDesktop = useIsDesktop();
  const { upload, isLoading: isUploading } = useUploadFile();
  const { mutateAsync: createGroup, isPending } = useCreateGroup();

  const form = useForm<CreateGroupFormData>({
    resolver: zodResolver(createGroupFormScheme),
    defaultValues: {
      UserIDs: [],
      GroupName: '',
    },
  });

  const memberIds = form.watch('UserIDs', []);

  const handleCreateGroup = async (data: CreateGroupFormData) => {
    const userId = getUserId();
    if (!userId) return Promise.reject();

    if (data.GroupImg) {
      data.GroupImg.url = await upload(data.GroupImg, 'avatar');
    }

    const result = await createGroup({
      GroupName: data.GroupName,
      GroupImg: data.GroupImg?.url,
      UserIDs: isEmpty(data.UserIDs) ? `${userId}|0` : data.UserIDs.concat([userId]).join('|'),
    });

    if (result?.Code === 0 && result.Data) {
      if (isDesktop) {
        navigate(-1);
      } else {
        navigate(ROUTES.MESSAGES.$path());
      }
    }
  };

  return (
    <PageLayout
      className='w-full'
      title={step === 0 ? t('conversation.list.menu.select-contact') : t('conversation.list.menu.create-group')}
      headerConfig={{
        navigateBack:
          step === 0
            ? true
            : () => {
                setStep(0);
              },
        actions: step ? null : (
          <ProgressButton
            as='button'
            className='btn btn-ghost btn-sm text-primary mode-embedded:text-primary-content layout-compact:text-primary'
            disabled={memberIds.length < 1}
            onClick={() => {
              setStep(1);
            }}
            label='common.confirm'
          />
        ),
      }}>
      <FormProvider {...form}>
        <CreateGroupForm
          step={step}
          onSubmit={form.handleSubmit(handleCreateGroup, console.error)}
        />
      </FormProvider>

      {step === 1 && (
        <div className='fixed bottom-0 left-0 right-0 p-4 z-10'>
          <ProgressButton
            as='submit'
            isLoading={isPending || isUploading}
            className='btn btn-primary w-full'
            onClick={form.handleSubmit(handleCreateGroup, console.error)}
            label='contact.create-group.create'
          />
        </div>
      )}
    </PageLayout>
  );
}
